import type {
  GroupedNodes,
  InputValue,
  JsonValue,
  NodeStruct,
} from '@/types/node-types';
import { useNodeStore } from '@/stores/node-store';
import { useWorkflowPanelStore } from '@/stores/workflow-panel-store';
import {
  getBranchHandleId,
  getControlTargetHandleId,
} from '@/pages/workflow/helper/functional-control-flow';
import { defaultFeedbackSlot } from '@/pages/workflow/helper/repeat-author';
import type { Condition } from '@/pages/workflow/helper/switch-condition-builder';
import { REPEAT_NODE_ID } from '@/pages/workflow/helper/repeat-control';
import {
  MAP_NODE_ID,
  attachMapBody,
  defaultMapConfig,
} from '@/pages/workflow/helper/map-control';
import { mapSupportsRegion } from '@/pages/workflow/helper/for-each-region';
import { defaultCollectPort } from '@/pages/workflow/helper/for-each-author';

/**
 * Insertable examples: small, valid graphs built only from the logic nodes
 * and plain inputs, drawn from the algorithm suite and the Together
 * packet's Claim Switch (labels only). Each is inserted through the normal
 * store actions (addNodes, handleParameterChange, onConnect, the branch
 * handles, the loop author), so what lands on the canvas is exactly what a
 * person could have built by hand.
 */

export interface TemplateNode {
  /** Key other parts of the template refer to. */
  key: string;
  nodeId: string;
  name?: string;
  description?: string;
  params?: Record<string, JsonValue>;
  /** Grid position in node widths / heights from the insertion point. */
  col: number;
  row: number;
  /** Skip this node (and edges touching it) when the catalog lacks it. */
  optional?: boolean;
  /** Only in the For-each body stamp (dropped when the Map runs one step). */
  forEachOnly?: boolean;
}

export interface TemplateEdge {
  from: string;
  /** Output port name on the source; the first port when omitted. */
  port?: string;
  to: string;
  /** Param field id on the target (bindable); the first input port when omitted. */
  param?: string;
}

export interface TemplateBranch {
  from: string;
  label: string;
  to: string;
}

export interface TemplateLoop {
  carrier: string;
  entry: string;
  judge: string;
  condition: Condition;
  maxIterations: number;
  feedbackPath: Array<string | number>;
}

export interface LogicTemplate {
  id: string;
  title: string;
  summary: string;
  /** Which logic node this example is mainly about. */
  nodeId: string;
  nodes: TemplateNode[];
  edges: TemplateEdge[];
  branches?: TemplateBranch[];
  loop?: TemplateLoop;
  /** Attach the Map Item body to this Map node. */
  mapBody?: { map: string; body: string };
  /**
   * Phase 8c: when the runtime's Map takes a region body and the entry node
   * is in the catalog, draw this For-each body instead of `mapBody`.
   */
  forEach?: { map: string; entry: string; exit: string; itemName?: string };
  /** The node to open after inserting. */
  focus: string;
}

const path = (...segments: Array<string | number>) => ({
  op: 'path',
  path: ['value', ...segments],
});
const lit = (value: JsonValue) => ({ op: 'literal', value });
const eq = (left: object, right: object) => ({ op: 'eq', left, right });
const gte = (left: object, right: object) => ({ op: 'gte', left, right });
const lt = (left: object, right: object) => ({ op: 'lt', left, right });
const and = (...args: object[]) => ({ op: 'and', args });

export const LOGIC_TEMPLATES: LogicTemplate[] = [
  // --- Switch ---------------------------------------------------------------
  {
    id: 'route-by-status',
    title: 'Route by status',
    summary:
      "A Switch sends an item down the branch that matches its status, with a default for anything else (the shape of the Together packet's Claim Switch).",
    nodeId: 'switch_node',
    nodes: [
      {
        key: 'input',
        nodeId: 'json_input_node',
        name: 'Claim result',
        params: {
          mode: 'value',
          value: { verdict: 'proceed', reason: 'new deal' },
        },
        col: 0,
        row: 0,
      },
      {
        key: 'switch',
        nodeId: 'switch_node',
        name: 'Route by verdict',
        params: {
          cases: [
            { label: 'proceed', when: eq(path('verdict'), lit('proceed')) },
            {
              label: 'short_circuit',
              when: {
                op: 'in',
                item: path('verdict'),
                collection: lit(['merged', 'in-flight']),
              },
            },
            { label: 'exhausted', when: eq(path('verdict'), lit('exhausted')) },
          ],
          null_behavior: 'default',
          default_label: 'gate_anomaly',
        },
        col: 1,
        row: 0,
      },
      {
        key: 'proceed',
        nodeId: 'text_output_node',
        name: 'Proceed',
        col: 2,
        row: -1,
        optional: true,
      },
      {
        key: 'review',
        nodeId: 'text_output_node',
        name: 'Review',
        col: 2,
        row: 1,
        optional: true,
      },
    ],
    edges: [{ from: 'input', port: 'value', to: 'switch', param: 'value' }],
    branches: [
      { from: 'switch', label: 'proceed', to: 'proceed' },
      { from: 'switch', label: 'short_circuit', to: 'review' },
      { from: 'switch', label: 'exhausted', to: 'review' },
      { from: 'switch', label: 'gate_anomaly', to: 'review' },
    ],
    focus: 'switch',
  },
  {
    id: 'route-by-score',
    title: 'Route by score',
    summary:
      'Numeric thresholds in order: 90 and up is an A, 80 to 89 a B, everything else a C (from algo-grade-bucket).',
    nodeId: 'switch_node',
    nodes: [
      {
        key: 'input',
        nodeId: 'json_input_node',
        name: 'Score',
        params: { mode: 'value', value: { score: 86 } },
        col: 0,
        row: 0,
      },
      {
        key: 'switch',
        nodeId: 'switch_node',
        name: 'Grade',
        params: {
          cases: [
            { label: 'a', when: gte(path('score'), lit(90)) },
            {
              label: 'b',
              when: and(
                gte(path('score'), lit(80)),
                lt(path('score'), lit(90)),
              ),
            },
          ],
          null_behavior: 'default',
          default_label: 'c',
        },
        col: 1,
        row: 0,
      },
    ],
    edges: [{ from: 'input', port: 'value', to: 'switch', param: 'value' }],
    focus: 'switch',
  },
  // --- Validate -------------------------------------------------------------
  {
    id: 'require-fields',
    title: 'Require these fields',
    summary:
      'Stop the run unless the value has a name, an email and a positive amount.',
    nodeId: 'validate_node',
    nodes: [
      {
        key: 'input',
        nodeId: 'json_input_node',
        name: 'Order',
        params: {
          mode: 'value',
          value: { name: 'Ada', email: 'ada@example.com', amount: 12 },
        },
        col: 0,
        row: 0,
      },
      {
        key: 'validate',
        nodeId: 'validate_node',
        name: 'Order is complete',
        params: {
          schema: {
            type: 'object',
            required: ['name', 'email', 'amount'],
            properties: {
              name: { type: 'string', minLength: 1 },
              email: { type: 'string', minLength: 3 },
              amount: { type: 'number', exclusiveMinimum: 0 },
            },
          },
          assertions: [],
          mode: 'fail',
          error_code: 'order.incomplete',
          error_message: 'The order is missing a required field',
        },
        col: 1,
        row: 0,
      },
    ],
    edges: [{ from: 'input', port: 'value', to: 'validate', param: 'value' }],
    focus: 'validate',
  },
  {
    id: 'check-then-route',
    title: 'Check, then route',
    summary:
      'Validate in collect mode passes the value through with is_valid; a Switch sends failures to review instead of stopping the run.',
    nodeId: 'validate_node',
    nodes: [
      {
        key: 'input',
        nodeId: 'json_input_node',
        name: 'Candidate',
        params: { mode: 'value', value: { score: 7.5, rounds: 2 } },
        col: 0,
        row: 0,
      },
      {
        key: 'validate',
        nodeId: 'validate_node',
        name: 'Good enough?',
        params: {
          schema: null,
          assertions: [
            {
              code: 'score_high_enough',
              message: 'The score must be at least 8',
              when: gte(path('score'), lit(8)),
            },
          ],
          mode: 'collect',
          error_code: 'validation.failed',
          error_message: 'Validation failed',
        },
        col: 1,
        row: 0,
      },
      {
        key: 'switch',
        nodeId: 'switch_node',
        name: 'Passed?',
        params: {
          cases: [
            {
              label: 'passed',
              when: eq({ op: 'path', path: ['value'] }, lit(true)),
            },
          ],
          null_behavior: 'default',
          default_label: 'needs_review',
        },
        col: 2,
        row: 0,
      },
    ],
    edges: [
      { from: 'input', port: 'value', to: 'validate', param: 'value' },
      { from: 'validate', port: 'is_valid', to: 'switch', param: 'value' },
    ],
    focus: 'validate',
  },
  // --- Classify / Decide ----------------------------------------------------
  {
    id: 'classify-then-route',
    title: 'Classify, then route',
    summary:
      'A model sorts the subject into urgent, routine or ignore; a Switch routes on the label.',
    nodeId: 'classify_decide_node',
    nodes: [
      {
        key: 'input',
        nodeId: 'json_input_node',
        name: 'Message',
        params: {
          mode: 'value',
          value: { text: 'Our invoice shows two charges for one seat.' },
        },
        col: 0,
        row: 0,
      },
      {
        key: 'classify',
        nodeId: 'classify_decide_node',
        name: 'Triage',
        params: {
          decision_preset: 'triage',
          decision_labels: ['urgent', 'routine', 'ignore'],
          criteria:
            "Choose 'urgent' when a customer is blocked or money is wrong, 'routine' for ordinary requests, 'ignore' for spam.",
        },
        col: 1,
        row: 0,
      },
      {
        key: 'switch',
        nodeId: 'switch_node',
        name: 'Route by label',
        params: {
          cases: [
            {
              label: 'urgent',
              when: eq({ op: 'path', path: ['value'] }, lit('urgent')),
            },
            {
              label: 'routine',
              when: eq({ op: 'path', path: ['value'] }, lit('routine')),
            },
            {
              label: 'ignore',
              when: eq({ op: 'path', path: ['value'] }, lit('ignore')),
            },
          ],
          null_behavior: 'error',
          default_label: '',
        },
        col: 2,
        row: 0,
      },
    ],
    edges: [
      { from: 'input', port: 'value', to: 'classify', param: 'subject' },
      { from: 'classify', port: 'decision', to: 'switch', param: 'value' },
    ],
    focus: 'classify',
  },
  // --- Repeat Until ---------------------------------------------------------
  {
    id: 'retry-until-approved',
    title: 'Retry the draft until the reviewer approves',
    summary:
      'An Agent drafts, a Classify reviewer says approved or needs_work, and the loop feeds the verdict back into the next draft, at most 3 times.',
    nodeId: 'repeat_until_node',
    nodes: [
      {
        key: 'brief',
        nodeId: 'json_input_node',
        name: 'Brief',
        params: {
          mode: 'value',
          value: { topic: 'Why the Q3 numbers moved', audience: 'the board' },
        },
        col: 0,
        row: 0,
      },
      {
        key: 'draft',
        nodeId: 'agent_node',
        name: 'Draft',
        params: {
          user_prompt:
            'Write a one-paragraph summary for the audience in the brief. If reviewer feedback is attached, address every point.',
        },
        col: 1,
        row: 0,
      },
      {
        key: 'review',
        nodeId: 'classify_decide_node',
        name: 'Reviewer',
        params: {
          decision_preset: 'custom',
          decision_labels: ['approved', 'needs_work'],
          criteria:
            "Choose 'approved' only if the paragraph answers the brief plainly and makes no unsupported claims; otherwise 'needs_work' and say what to fix.",
        },
        col: 2,
        row: 0,
      },
      {
        key: 'loop',
        nodeId: REPEAT_NODE_ID,
        name: 'Until approved',
        col: 3,
        row: 0,
      },
    ],
    edges: [
      { from: 'brief', port: 'value', to: 'draft', param: 'user_prompt' },
      { from: 'draft', port: 'text', to: 'review', param: 'subject' },
    ],
    loop: {
      carrier: 'loop',
      entry: 'draft',
      judge: 'review',
      condition: {
        kind: 'rule',
        field: ['decision'],
        op: 'eq',
        value: 'approved',
        guardMissing: false,
      },
      maxIterations: 3,
      feedbackPath: ['result'],
    },
    focus: 'loop',
  },
  // --- Union / Reduce -------------------------------------------------------
  {
    id: 'collect-three-sources',
    title: 'Collect results from three sources',
    summary:
      'Three outputs gathered into one list in connection order, then counted.',
    nodeId: 'union_collect_node',
    nodes: [
      {
        key: 'a',
        nodeId: 'json_input_node',
        name: 'Source A',
        params: { mode: 'value', value: ['ada', 'grace'] },
        col: 0,
        row: -1,
      },
      {
        key: 'b',
        nodeId: 'json_input_node',
        name: 'Source B',
        params: { mode: 'value', value: ['linus'] },
        col: 0,
        row: 0,
      },
      {
        key: 'c',
        nodeId: 'json_input_node',
        name: 'Source C',
        params: { mode: 'value', value: ['grace'] },
        col: 0,
        row: 1,
      },
      {
        key: 'union',
        nodeId: 'union_collect_node',
        name: 'All names',
        params: {
          mode: 'union',
          distinct_mode: 'canonical_value',
          null_handling: 'drop',
        },
        col: 1,
        row: 0,
      },
      {
        key: 'count',
        nodeId: 'reduce_node',
        name: 'How many',
        params: { operation: 'count' },
        col: 2,
        row: 0,
      },
    ],
    edges: [
      { from: 'a', port: 'value', to: 'union', param: 'values' },
      { from: 'b', port: 'value', to: 'union', param: 'values' },
      { from: 'c', port: 'value', to: 'union', param: 'values' },
      { from: 'union', port: 'items', to: 'count', param: 'items' },
    ],
    focus: 'union',
  },
  {
    id: 'dedupe-by-field',
    title: 'Remove duplicates by a field',
    summary:
      'Two lists of records merged, keeping the first record for each id (from algo-dedup-by-key).',
    nodeId: 'union_collect_node',
    nodes: [
      {
        key: 'a',
        nodeId: 'json_input_node',
        name: 'Batch 1',
        params: {
          mode: 'value',
          value: [
            { id: 1, name: 'Ada' },
            { id: 2, name: 'Grace' },
          ],
        },
        col: 0,
        row: -1,
      },
      {
        key: 'b',
        nodeId: 'json_input_node',
        name: 'Batch 2',
        params: {
          mode: 'value',
          value: [
            { id: 2, name: 'Grace H.' },
            { id: 3, name: 'Linus' },
          ],
        },
        col: 0,
        row: 1,
      },
      {
        key: 'union',
        nodeId: 'union_collect_node',
        name: 'Unique by id',
        params: {
          mode: 'union',
          distinct_mode: 'key_path',
          key_path: '/id',
          null_handling: 'drop',
        },
        col: 1,
        row: 0,
      },
    ],
    edges: [
      { from: 'a', port: 'value', to: 'union', param: 'values' },
      { from: 'b', port: 'value', to: 'union', param: 'values' },
    ],
    focus: 'union',
  },
  {
    id: 'sum-a-list',
    title: 'Sum a list',
    summary:
      "Fold a list of numbers into their total (from algo-factorial's Reduce variant).",
    nodeId: 'reduce_node',
    nodes: [
      {
        key: 'input',
        nodeId: 'json_input_node',
        name: 'Amounts',
        params: { mode: 'value', value: [12, 7.5, 30] },
        col: 0,
        row: 0,
      },
      {
        key: 'sum',
        nodeId: 'reduce_node',
        name: 'Total',
        params: { operation: 'sum' },
        col: 1,
        row: 0,
      },
    ],
    edges: [{ from: 'input', port: 'value', to: 'sum', param: 'items' }],
    focus: 'sum',
  },
  // --- Map ------------------------------------------------------------------
  {
    id: 'for-each-item',
    title: 'For each item',
    summary:
      'Run a step once per entry and collect the results in input order (from algo-trial-division). Where the runtime supports it, the step is a part of the canvas.',
    nodeId: MAP_NODE_ID,
    nodes: [
      {
        key: 'input',
        nodeId: 'json_input_node',
        name: 'Candidates',
        params: { mode: 'value', value: [2, 3, 4, 5, 6] },
        col: 0,
        row: 0,
      },
      {
        key: 'map',
        nodeId: MAP_NODE_ID,
        name: 'Each candidate',
        col: 1,
        row: 0,
      },
      {
        key: 'square',
        nodeId: 'calculate_node',
        name: 'Square',
        params: { outputs: [{ name: 'square', formula: 'value * value' }] },
        col: 1,
        row: 1,
        optional: true,
        forEachOnly: true,
      },
      {
        key: 'count',
        nodeId: 'reduce_node',
        name: 'How many',
        params: { operation: 'count' },
        col: 2,
        row: 0,
      },
    ],
    edges: [
      { from: 'input', port: 'value', to: 'map', param: 'items' },
      { from: 'map', port: 'results', to: 'count', param: 'items' },
    ],
    mapBody: { map: 'map', body: 'map_item_node' },
    forEach: {
      map: 'map',
      entry: 'square',
      exit: 'square',
      itemName: 'candidate',
    },
    focus: 'map',
  },
];

export const templatesForNode = (nodeId: string): LogicTemplate[] =>
  LOGIC_TEMPLATES.filter((template) => template.nodeId === nodeId);

const COL_WIDTH = 380;
const ROW_HEIGHT = 260;

const catalogOf = (grouped: GroupedNodes): NodeStruct[] =>
  Object.values(grouped).flat();

export interface TemplateAvailability {
  available: boolean;
  missing: string[];
}

/** Which required nodes the catalog lacks; optional nodes never block. */
export const templateAvailability = (
  template: LogicTemplate,
  grouped: GroupedNodes,
): TemplateAvailability => {
  const catalog = catalogOf(grouped);
  const have = new Set(catalog.map((entry) => entry.node_id));
  const missing = template.nodes
    .filter((node) => !node.optional && !have.has(node.nodeId))
    .map((node) => node.nodeId);
  if (
    template.mapBody &&
    !have.has(template.mapBody.body) &&
    !forEachStampPossible(template, catalog)
  ) {
    missing.push(template.mapBody.body);
  }
  return { available: missing.length === 0, missing };
};

/**
 * Can this template draw its For-each body? The runtime's Map must accept a
 * region (config 1.1) and the body's first and last nodes must be in the
 * catalog; otherwise it falls back to the single-step `mapBody`.
 */
export const forEachStampPossible = (
  template: LogicTemplate,
  catalog: readonly NodeStruct[],
): boolean => {
  if (!template.forEach) return false;
  const have = new Set(catalog.map((entry) => entry.node_id));
  const nodeIdOf = (key: string) =>
    template.nodes.find((node) => node.key === key)?.nodeId;
  const entry = nodeIdOf(template.forEach.entry);
  const exit = nodeIdOf(template.forEach.exit);
  return (
    mapSupportsRegion(catalog.find((entry) => entry.node_id === MAP_NODE_ID)) &&
    Boolean(entry && have.has(entry) && exit && have.has(exit))
  );
};

const portId = (
  struct: NodeStruct,
  name: string | undefined,
): string | null => {
  const ports = struct.output_ports ?? [];
  const port = name ? ports.find((entry) => entry.name === name) : ports[0];
  return port?.id ?? null;
};

const paramPortId = (
  struct: NodeStruct,
  fieldId: string | undefined,
): string | null => {
  if (fieldId) {
    return (
      struct.params?.find((param) => param.field_id === fieldId)
        ?.bindable_config?.input_port?.id ?? null
    );
  }
  return struct.input_ports?.[0]?.id ?? null;
};

export interface InsertedTemplate {
  ids: Record<string, string>;
  focusId: string | null;
}

/**
 * Insert a template at `position` through the store. Returns the ids of the
 * nodes it created (by template key), or null when a required node is
 * missing from the catalog.
 */
export const insertTemplate = (
  template: LogicTemplate,
  grouped: GroupedNodes,
  position: { x: number; y: number },
): InsertedTemplate | null => {
  if (!templateAvailability(template, grouped).available) return null;
  const catalog = catalogOf(grouped);
  const structFor = (nodeId: string) =>
    catalog.find((entry) => entry.node_id === nodeId);
  const stampForEach = forEachStampPossible(template, catalog);
  const present = template.nodes.filter(
    (node) => structFor(node.nodeId) && (stampForEach || !node.forEachOnly),
  );
  const store = useNodeStore.getState();
  const created = store.addNodes(
    present.map((node) => ({
      struct: structFor(node.nodeId)!,
      position: {
        x: position.x + node.col * COL_WIDTH,
        y: position.y + node.row * ROW_HEIGHT,
      },
    })),
  );
  const ids: Record<string, string> = {};
  present.forEach((node, index) => {
    ids[node.key] = created[index];
  });

  // Names, descriptions and params.
  for (const node of present) {
    const id = ids[node.key];
    const fresh = useNodeStore.getState();
    if (node.name || node.description) {
      fresh.updateNodeAttributes(id, {
        ...(node.name ? { user_config_name: node.name } : {}),
        ...(node.description
          ? { user_config_description: node.description }
          : {}),
      });
    }
    for (const [fieldId, value] of Object.entries(node.params ?? {})) {
      fresh.handleParameterChange(id, fieldId, value as unknown as InputValue);
    }
  }

  // Map body: a drawn For-each body when possible, else one Map Item call.
  if (template.forEach && stampForEach) {
    const mapId = ids[template.forEach.map];
    const entryId = ids[template.forEach.entry];
    const exitId = ids[template.forEach.exit];
    if (mapId && entryId && exitId) {
      const fresh = useNodeStore.getState();
      const exitNode = fresh.nodes.find((node) => node.id === exitId);
      fresh.applyForEachSentence(mapId, {
        entryId,
        exitId,
        collectPortId: defaultCollectPort(exitNode),
        itemName: template.forEach.itemName ?? 'item',
      });
    }
  } else if (template.mapBody) {
    const mapId = ids[template.mapBody.map];
    const mapStruct = structFor(MAP_NODE_ID);
    const bodyStruct = structFor(template.mapBody.body);
    if (mapId && mapStruct && bodyStruct) {
      const attached = attachMapBody(
        defaultMapConfig(mapId, mapStruct),
        bodyStruct,
      );
      if (attached.ok) {
        useNodeStore
          .getState()
          .handleParameterChange(
            mapId,
            'map_config',
            attached.config as unknown as InputValue,
          );
      }
    }
  }

  // Data edges.
  for (const edge of template.edges) {
    const sourceId = ids[edge.from];
    const targetId = ids[edge.to];
    if (!sourceId || !targetId) continue;
    const sourceStruct = structFor(
      present.find((n) => n.key === edge.from)!.nodeId,
    )!;
    const targetStruct = structFor(
      present.find((n) => n.key === edge.to)!.nodeId,
    )!;
    const sourceHandle = portId(sourceStruct, edge.port);
    const targetHandle = paramPortId(targetStruct, edge.param);
    if (!sourceHandle || !targetHandle) continue;
    useNodeStore.getState().onConnect({
      source: sourceId,
      sourceHandle,
      target: targetId,
      targetHandle,
    });
  }

  // Branch edges from the Switch's labelled handles.
  for (const branch of template.branches ?? []) {
    const sourceId = ids[branch.from];
    const targetId = ids[branch.to];
    if (!sourceId || !targetId) continue;
    useNodeStore.getState().onConnect({
      source: sourceId,
      sourceHandle: getBranchHandleId(branch.label),
      target: targetId,
      targetHandle: getControlTargetHandleId(targetId),
    });
  }

  // The loop, through the Phase 3 author.
  if (template.loop) {
    const carrierId = ids[template.loop.carrier];
    const entryId = ids[template.loop.entry];
    const judgeId = ids[template.loop.judge];
    const entryStruct = structFor(
      present.find((n) => n.key === template.loop!.entry)!.nodeId,
    );
    if (carrierId && entryId && judgeId) {
      useNodeStore.getState().applyLoopSentence(carrierId, {
        entryId,
        judgeId,
        condition: template.loop.condition,
        maxIterations: template.loop.maxIterations,
        feedbackPath: template.loop.feedbackPath,
        feedbackSlot: defaultFeedbackSlot(entryStruct, catalog),
      });
    }
  }

  const focusId = ids[template.focus] ?? null;
  if (focusId) {
    useNodeStore.getState().selectNode(focusId);
    const panel = useWorkflowPanelStore.getState();
    panel.openWorkflowPanel('configure');
    panel.setConsolePanelOpen(true);
  }
  return { ids, focusId };
};
